// src/pages/Menu/Articulacion/Statutes.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FileText,
  CalendarClock,
  ArrowRightCircle,
  Calendar,
} from "lucide-react";
import { Banner } from "../../../components/common/Banner";
import * as Svc from "../../../services/statutesService";

/* ---------- Helpers ---------- */
const absoluteUrl = (href) => {
  try {
    return new URL(href, window.location.origin).href;
  } catch {
    return href;
  }
};

const DEFAULT_PREVIOUS_TITLE = "ESTATUTOS";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // Solo fecha, sin hora
  return d.toLocaleDateString("es-CL", { dateStyle: "medium" });
};

/* ---------- Mini UI helpers ---------- */
const Pill = ({ children, className = "" }) => (
  <span
    className={
      "inline-flex items-center gap-2 rounded-full bg-gray-50 border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 " +
      className
    }
  >
    {children}
  </span>
);

const PdfIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M6 2h8l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
      stroke="#0B4A8F"
      strokeWidth="1.6"
    />
    <path d="M14 2v4h4" stroke="#0B4A8F" strokeWidth="1.6" />
    <text x="7.5" y="16" fontSize="7" fontWeight="700" fill="#0B4A8F">
      PDF
    </text>
  </svg>
);

const DocRow = ({ doc }) => {
  const url = doc.secureUrl;
  const label = doc.title?.trim() || `Documento ${doc.year ?? ""}`;
  const mb = doc.bytes && typeof doc.bytes === "number" && !Number.isNaN(doc.bytes)
    ? (doc.bytes / (1024 * 1024)).toFixed(2)
    : undefined;

  const Inner = (
    <div className="group flex items-center justify-between gap-3 text-sm">
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 border border-blue-100 group-hover:bg-[#0B4A8F] group-hover:border-[#0B4A8F] transition-colors">
          <PdfIcon size={16} />
        </span>
        <span className="font-semibold text-[#0B4A8F] group-hover:underline truncate max-w-[220px] sm:max-w-[260px]">
          {label}
        </span>
        {doc.version && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-gray-100 border text-gray-700">
            v{doc.version}
          </span>
        )}
        {mb && <span className="text-xs text-gray-500">({mb} MB)</span>}
      </div>

      {url && (
        <ArrowRightCircle className="w-4 h-4 text-gray-300 group-hover:text-[#0B4A8F] flex-shrink-0" />
      )}
    </div>
  );

  if (!url) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white/80 px-3 py-2">
        {Inner}
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="block rounded-xl border border-gray-200 bg-white/90 px-3 py-2 hover:border-[#0B4A8F] hover:shadow-sm"
      title={label}
    >
      {Inner}
    </a>
  );
};

export default function Statutes() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState({
    pageTitle: "Estatutos y documentos",
    subtitle: "",
    previousTitle: DEFAULT_PREVIOUS_TITLE,
    principlesVisible: true,
    ctaEnabled: false,
    ctaLabel: "",
    ctaUrl: "",
  });
  const [lastVisibleAt, setLastVisibleAt] = useState(null);
  const [filterYear, setFilterYear] = useState("");

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const { items: list, config: cfg } = await Svc.listPublicStatutes();
        if (!live) return;
        setItems(Array.isArray(list) ? list : []);
        setConfig(cfg || {});
        setLastVisibleAt(cfg?.lastVisibleAt || null);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const groupsByYear = useMemo(() => {
    const map = new Map();
    (items || []).forEach((r) => {
      const y = r.year ?? "Sin año";
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(r);
    });
    // ordenar por posición ascendente
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }
    return map;
  }, [items]);

  const yearsSorted = useMemo(() => {
    const ys = Array.from(groupsByYear.keys());
    return ys.sort((a, b) => {
      if (a === "Sin año") return 1;
      if (b === "Sin año") return -1;
      return Number(b) - Number(a);
    });
  }, [groupsByYear]);

  // Años que se muestran según filtro
  const filteredYears = useMemo(
    () =>
      filterYear
        ? yearsSorted.filter((y) => String(y) === filterYear)
        : yearsSorted,
    [yearsSorted, filterYear]
  );

  const featuredYear =
    !filterYear && filteredYears.length > 0 ? filteredYears[0] : null;
  const featuredDocs = featuredYear
    ? groupsByYear.get(featuredYear) || []
    : [];
  const featuredHeadline =
    (config?.yearHeadlines && config.yearHeadlines[featuredYear]) ||
    (featuredYear && `Estatutos ${featuredYear}`);

  const otherYears = !filterYear
    ? filteredYears.slice(1)
    : filteredYears;

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8 space-y-6">
      <Banner
        title={config?.pageTitle || "Estatutos y documentos"}
        message={config?.subtitle || "Documentos oficiales de la organización"}
        className="mb-2"
      />

      {/* Panel superior: última actualización + filtro por año */}
      <div className="rounded-2xl bg-gradient-to-r from-[#E6F0FF] via-[#F3FAFF] to-[#E8F9FF] p-4 md:p-5 border border-blue-100/70 mb-4">
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-5">
          {/* Última actualización */}
          {lastVisibleAt && (
            <div className="inline-flex items-center gap-2 text-sm text-gray-700 bg-white/80 px-3.5 py-2 rounded-xl border border-gray-200 shadow-sm">
              <CalendarClock className="w-4 h-4 text-[#0B4A8F]" />
              <span className="font-medium">Última actualización visible</span>
              <span className="font-semibold text-gray-900">
                {fmtDate(lastVisibleAt)}
              </span>
            </div>
          )}

          {/* Separador visual */}
          {lastVisibleAt && (
            <div className="hidden md:block w-px h-8 bg-gray-300" />
          )}

          {/* Filtro por año */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 whitespace-nowrap">
              <Calendar className="w-4 h-4 text-[#0B4A8F]" />
              Filtrar por año
            </div>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="w-[180px] px-3 py-2 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none text-sm"
            >
              <option value="">Todos los años</option>
              {yearsSorted.map((y) => (
                <option key={String(y)} value={String(y)}>
                  {y === "Sin año" ? "Sin año" : y}
                </option>
              ))}
            </select>
            {filterYear && (
              <button
                type="button"
                onClick={() => setFilterYear("")}
                className="text-xs text-[#0488AF] hover:underline whitespace-nowrap"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      {loading ? (
        <div className="py-12 text-center text-gray-500 text-sm">
          Cargando documentos…
        </div>
      ) : filteredYears.length === 0 ? (
        <div className="py-12 text-center text-gray-500 text-sm">
          No hay estatutos ni documentos publicados por ahora.
        </div>
      ) : (
        <>
          {/* Bloque destacado (año más reciente, solo sin filtro) */}
          {featuredYear && featuredDocs.length > 0 && (
            <section className="rounded-3xl bg-gradient-to-t from-[#0EC5DF] via-[#0488AF] to-[#0B4A8F] text-white px-5 py-6 md:px-7 md:py-7 shadow-sm mb-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-baseline gap-3">
                  <span className="text-xs uppercase tracking-[0.18em] font-semibold text-white/70">
                    Estatutos vigentes
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-white/15 border border-white/30">
                    {featuredYear === "Sin año"
                      ? "Sin año definido"
                      : featuredYear}
                  </span>
                </div>
              </div>

              <div className="grid gap-5 md:gap-7 md:grid-cols-[minmax(240px,280px),minmax(0,1fr)] items-start">
                {/* Portada */}
                {featuredDocs[0]?.coverUrl && (
                  <div className="w-full max-w-[280px] justify-self-center md:justify-self-start">
                    <div className="aspect-[3/4] rounded-2xl bg-white overflow-hidden shadow-md">
                      <img
                        className="w-full h-full object-cover"
                        src={featuredDocs[0].coverUrl}
                        alt={`Estatutos ${featuredYear}`}
                        onError={(e) => {
                          const href = featuredDocs[0]?.secureUrl || (featuredDocs[0]?.id ? Svc.downloadStatuteUrl(featuredDocs[0].id) : null);
                          if (href) {
                            e.currentTarget.parentElement.innerHTML = `<embed type="application/pdf" src="${absoluteUrl(href)}#page=1&zoom=125" style="width:100%;height:100%;display:block;border:0" />`;
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Título + lista */}
                <div className="w-full min-w-0">
                  <div className="text-xl md:text-2xl font-semibold mb-3">
                    {featuredHeadline}
                  </div>
                  <div className="space-y-2.5">
                    {featuredDocs.map((doc) => (
                      <div
                        key={doc.id}
                        className="rounded-xl bg-white/95 text-gray-900 px-3 py-2 shadow-sm"
                      >
                        <DocRow doc={doc} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Otros años (o el año filtrado si hay filtro) */}
          {otherYears.length > 0 && !filterYear && (
            <div className="mb-2">
              <div className="text-sm font-semibold tracking-[0.18em] text-[#C42251] mb-1 uppercase">
                {config?.previousTitle?.trim() || DEFAULT_PREVIOUS_TITLE}
              </div>
              <hr className="border-gray-200" />
            </div>
          )}

          {otherYears.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherYears.map((y) => {
                const docs = groupsByYear.get(y) || [];
                if (!docs.length) return null;
                return (
                  <section
                    key={String(y)}
                    className="rounded-2xl bg-white ring-1 ring-black/5 p-5 md:p-6 flex flex-col gap-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-lg md:text-xl font-bold text-gray-800">
                        {y === "Sin año" ? "Sin año" : y}
                      </div>
                      <Pill className="text-xs bg-gray-50 border-gray-200">
                        {docs.length} documento
                        {docs.length === 1 ? "" : "s"}
                      </Pill>
                    </div>
                    <div className="flex flex-col gap-3.5 mt-1">
                      {docs.map((doc) => (
                        <DocRow key={doc.id} doc={doc} />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}

          {/* Caso filtro: si solo hay un año y no entra en otherYears (por estructura), lo manejamos aquí */}
          {filterYear && !featuredYear && otherYears.length === 0 && (
            <div className="text-gray-500 text-sm">
              No se encontraron documentos para el año seleccionado.
            </div>
          )}
        </>
      )}
    </div>
  );
}
